import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { LogLevel } from '../models/settings.model';

@Injectable({
  providedIn: 'root'
})
export class LogService {
  private supabase = inject(SupabaseService).client;

  async log(level: LogLevel, message: string, metadata: object = {}): Promise<void> {
    try {
      const { error } = await this.supabase.rpc('log_event', {
        p_level: level,
        p_message: message,
        p_metadata: metadata || {}
      });

      if (error) {
        const normalizedMessage = String(error.message ?? '').toLowerCase();
        if (normalizedMessage.includes('aborterror') || normalizedMessage.includes('signal is aborted')) {
          return;
        }
        return;
      }
    } catch (e) {
      const messageText = e instanceof Error ? e.message.toLowerCase() : String(e).toLowerCase();
      if (messageText.includes('aborterror') || messageText.includes('signal is aborted')) {
        return;
      }
      return;
    }
  }

  async logInfo(message: string, metadata: Record<string, unknown> = {}): Promise<void> {
    await this.log('INFO', message, this.normalizeMetadata(metadata));
  }

  async logWarn(message: string, metadata: Record<string, unknown> = {}): Promise<void> {
    await this.log('WARN', message, this.normalizeMetadata(metadata));
  }

  async logError(message: string, error?: unknown, metadata: Record<string, unknown> = {}): Promise<void> {
    const payload = {
      ...this.normalizeMetadata(metadata),
      error: this.normalizeError(error),
    };

    await this.log('ERROR', message, payload);
  }

  private normalizeMetadata(metadata: Record<string, unknown> | unknown): Record<string, unknown> {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      return {};
    }

    try {
      return JSON.parse(JSON.stringify(metadata)) as Record<string, unknown>;
    } catch {
      return { serialization: 'failed' };
    }
  }

  private normalizeError(error: unknown): Record<string, unknown> {
    if (!error) {
      return {};
    }

    if (error instanceof Error) {
      const typedError = error as Error & {
        code?: string | number;
        status?: number;
        hint?: string;
        details?: string;
      };

      return {
        name: typedError.name,
        message: typedError.message,
        stack: typedError.stack ?? null,
        code: typedError.code ?? null,
        status: typedError.status ?? null,
        hint: typedError.hint ?? null,
        details: typedError.details ?? null,
      };
    }

    if (typeof error === 'object') {
      try {
        return JSON.parse(JSON.stringify(error)) as Record<string, unknown>;
      } catch {
        return { message: '[unserializable-error-object]' };
      }
    }

    return { message: String(error) };
  }
}
